import { type Component, For, Show } from "solid-js";
import CreditsIcon from "~/Icons/16_Icon_Credits.svg?component-solid";
import ArrowTipIcon from "~/Icons/Icon_ArrowTip.svg?component-solid";
import type { Coordinates, EntitySize } from "~/solidJs/shared/helpers/types/ISizeble";
import { Badge } from "~/solidJs/shared/ui/atoms/Badge/Badge";
import { StyledTooltip } from "~/solidJs/shared/ui/atoms/Tooltip";
import { useCreditsBadgePopover } from "./methods";
import type { CostDetailItem } from "./model";

interface CreditsBadgePopoverComponentProps {
	isOpen: () => boolean;
	position: () => Coordinates & EntitySize;

	costDetailItems: () => CostDetailItem[];
	onClose: () => void;
}

const CostDetailListItem: Component<{ item: CostDetailItem }> = (props) => {
	return (
		<div class="flex items-center justify-between gap-1">
			<div class="flex items-center gap-1">
				<div class="flex items-center p-1">
					<div class="h-1 w-1 rounded-full bg-white" />
				</div>
				<div class="flex flex-1 items-center gap-0.5">
					<span class="label-10-regular text-white">{props.item.name}</span>
					{props.item.value && (
						<span class="label-10-regular text-text-tertiary">
							({props.item.value})
						</span>
					)}
				</div>
			</div>
			<Badge variant="creditsDark" size="small">
				<CreditsIcon class="size-2.5" />
				<span class="caption-9-medium text-center text-white">{props.item.price}</span>
			</Badge>
		</div>
	);
};

export const CreditsBadgePopover: Component<CreditsBadgePopoverComponentProps> = (props) => {
	return (
		<Show when={props.isOpen()}>
			<StyledTooltip.Root
				defaultOpen={true}
				onOpenChange={(d) => {
					if (!d.open) {
						props.onClose();
					}
				}}
				positioning={{
					placement: "top",
					gutter: 35
				}}
			>
				<StyledTooltip.Trigger
					style={{
						left: `${props.position().left}px`,
						top: `${props.position().top}px`,
						width: `${props.position().width}px`,
						height: `${props.position().height}px`
					}}
					class="pointer-events-auto absolute z-[1000]"
				/>
				<StyledTooltip.Positioner>
					<StyledTooltip.Content class="relative rounded-lg border-none bg-background-black px-3 py-2.5 shadow-lg">
						<div class="mb-2">
							<h3 class="caption-13-medium text-white">
								What does the generation cost include?
							</h3>
						</div>
						<div class="flex flex-col gap-1.5">
							<For each={props.costDetailItems()}>
								{(item) => <CostDetailListItem item={item} />}
							</For>
						</div>
					</StyledTooltip.Content>
					<StyledTooltip.Arrow
						style={{
							"--arrow-offset-shifter": "calc(var(--arrow-size-half) - 2px)",
							"--arrow-offset": "calc(var(--arrow-offset-shifter) * -1)"
						}}
					>
						<StyledTooltip.ArrowTip>
							<ArrowTipIcon class="size-[var(--arrow-size)] rotate-[315deg]" />
						</StyledTooltip.ArrowTip>
					</StyledTooltip.Arrow>
				</StyledTooltip.Positioner>
			</StyledTooltip.Root>
		</Show>
	);
};

export const CreditsBadgePopoverPalette: Component = () => {
	const { isOpen, position, costDetailItems } = useCreditsBadgePopover();

	return (
		<CreditsBadgePopover
			isOpen={isOpen}
			position={position}
			costDetailItems={costDetailItems}
			onClose={() => useCreditsBadgePopover().setIsOpen(false)}
		/>
	);
};
